import { AbilityBuilder, AbilityClass, PureAbility } from '@casl/ability';

import type { LcmUserSession } from './types/next-auth';
import { canViewIdp, getIdpEligibilityFromUser } from './idp-eligibility';

enum Action {
  Create = 'create',
  Edit = 'edit',
  View = 'view',
  List = 'list',
  Manage = 'manage',
  Vote = 'vote',
  Facilitate = 'facilitate',
  Proxy = 'proxy',
  Unlock = 'unlock',
  Skip = 'skip',
  Delete = 'delete',
  Download = 'download',
}

export type Entities =
  | 'Nominations'
  | 'NominationsDashboard'
  | 'AssessmentsDashboard'
  | 'NominationsDashboardMenu'
  | 'AssessmentsDashboardMenu'
  | 'DashboardAsManager'
  | 'DashboardAsAdmin'
  | 'Admin'
  | 'Home'
  | 'DownloadDocuments'
  | 'Proxy'
  | 'User'
  | 'RoleSetting'
  | 'ManagerApproval'
  | 'Evaluations'
  | 'Overview'
  | 'FeatureFlags'
  | 'NominationsControls'
  | 'Reports'
  | 'Cheershub'
  | 'Footer'
  | 'Stellar'
  | 'Idp'
  | 'IdpEligibility'
  | 'TeamReports'
  | 'TeamIdp'
  | 'TeamAssessments'
  | 'Results'
  | 'SelfResults';

export type AbilityTuple = [`${Action}`, Entities | Record<string, unknown>];

export type AppAbility = PureAbility<AbilityTuple>;

export function createUserAbility(
  user?: LcmUserSession['user'],
  rules?: AppAbility['_indexedRules']
) {
  const { can, build } = new AbilityBuilder(
    PureAbility as AbilityClass<AppAbility>
  );

  if (rules) {
    const ability = build();
    ability.update(rules);

    return ability;
  }

  const notProxiedUserRoles = user?.roles;
  const canBeProxiedUserRoles = user?.proxiedAs?.roles ?? user?.roles;

  const isManager = user?.proxiedAs?.isManager ?? user?.isManager;

  const isLiveManager = user?.proxiedAs?.isLiveManager ?? user?.isLiveManager;

  const isParticipant = user?.proxiedAs?.isParticipant ?? user?.isParticipant;

  const isInEvaluationPeriod =
    user?.proxiedAs?.processStatus?.isInEvaluationPeriod ??
    user?.processStatus?.isInEvaluationPeriod;

  const hasCheersHubPermission =
    user?.proxiedAs?.cheersHubPermission ?? user?.cheersHubPermission;

  const hasDownloadDocumentsPermission =
    user?.proxiedAs?.downloadDocumentsPermission ??
    user?.downloadDocumentsPermission;

  const hasTheNominationsPeriodStarted =
    user?.proxiedAs?.processStatus?.hasTheNominationsPeriodStarted ??
    user?.processStatus?.hasTheNominationsPeriodStarted;

  const isInReportPeriod =
    user?.proxiedAs?.processStatus?.isInReportPeriod ??
    user?.processStatus?.isInReportPeriod;

  const isInHideNominationScreenPeriod =
    user?.proxiedAs?.processStatus?.isInHideNominationScreenPeriod ??
    user?.processStatus?.isInHideNominationScreenPeriod;

  const isInNominationsReadOnlyPeriod =
    user?.proxiedAs?.processStatus?.isInNominationsReadOnlyPeriod ??
    user?.processStatus?.isInNominationsReadOnlyPeriod;

  const isInNominationsPeriod =
    user?.proxiedAs?.processStatus?.isInNominationsPeriod ??
    user?.processStatus?.isInNominationsPeriod;

  const isInEvaluationReadOnlyPeriod =
    user?.proxiedAs?.processStatus?.isInEvaluationReadOnlyPeriod ??
    user?.processStatus?.isInEvaluationReadOnlyPeriod;

  const isInCalculationPeriod =
    user?.proxiedAs?.isInCalculationPeriod ?? user?.isInCalculationPeriod;

  const canSeeEvaluations =
    user?.proxiedAs?.canSeeEvaluations ?? user?.canSeeEvaluations;

  const canSeeEvaluationsOverview =
    user?.proxiedAs?.canSeeEvaluationsOverview ??
    user?.canSeeEvaluationsOverview;

  const isAdminOrPbp =
    canBeProxiedUserRoles?.IS_LCM_GLOBAL_ADMIN ||
    canBeProxiedUserRoles?.IS_LCM_ZONE_ADMIN ||
    canBeProxiedUserRoles?.IS_PBP;

  const isAdminGlobal = canBeProxiedUserRoles?.IS_LCM_GLOBAL_ADMIN;

  const hasMyTeamIdpPermission =
    user?.proxiedAs?.canSeeMyTeamIdp ?? user?.canSeeMyTeamIdp;
  const hasMyTeamResultsPermission =
    user?.proxiedAs?.canSeeMyTeamResults ?? user?.canSeeMyTeamResults;

  const canAccessResultsBeforeStart =
    user?.proxiedAs?.canAccessResultsBeforeStart ??
    user?.canAccessResultsBeforeStart;

  const idpEligibility = getIdpEligibilityFromUser(user);
  const hasIdpPermission = canViewIdp(idpEligibility);

  const canSeeReports = isParticipant || isAdminOrPbp;
  const canSeeMyTeamCompletion =
    user?.proxiedAs?.canSeeMyTeamCompletion ?? user?.canSeeMyTeamCompletion;

  const canSeeSelfResults =
    user?.proxiedAs?.canSeeSelfResults ?? user?.canSeeSelfResults;

  const canAccessDashboard =
    user?.proxiedAs?.canAccessDashboard ?? user?.canAccessDashboard;

  const canAccessDashboardAsManager =
    user?.proxiedAs?.canAccessDashboardAsManager ??
    user?.canAccessDashboardAsManager;

  can(Action.View, 'Home');

  if (hasCheersHubPermission) {
    can(Action.View, 'Cheershub');
  }

  if (
    canSeeEvaluations &&
    (isInEvaluationPeriod || isInEvaluationReadOnlyPeriod)
  ) {
    can(Action.View, 'Evaluations');
  }

  if (isInEvaluationPeriod && !isInEvaluationReadOnlyPeriod) {
    can(Action.Edit, 'Evaluations');
  }

  if (canSeeEvaluations && canSeeEvaluationsOverview) {
    can(Action.View, 'Overview');
  }

  if (hasTheNominationsPeriodStarted && !isInNominationsReadOnlyPeriod) {
    can(Action.View, 'NominationsControls');
  }

  if (
    isParticipant &&
    !isInHideNominationScreenPeriod &&
    (hasTheNominationsPeriodStarted || isInNominationsReadOnlyPeriod)
  ) {
    can(Action.View, 'Nominations');
  }

  if (
    (isInCalculationPeriod && canAccessResultsBeforeStart) ||
    (!isInCalculationPeriod && (canSeeReports || hasMyTeamResultsPermission))
  ) {
    can(Action.View, 'Reports');
    can(Action.View, 'Results');
  }

  if (canSeeReports && isInReportPeriod) {
    can(Action.Download, 'Reports');
  }

  if (isAdminOrPbp || isLiveManager || isAdminGlobal) {
    can(Action.Manage, 'Reports');
  }

  if (canSeeSelfResults && canAccessResultsBeforeStart) {
    can(Action.View, 'SelfResults');
  }

  if (notProxiedUserRoles?.IS_LCM_GLOBAL_ADMIN) {
    can(Action.Proxy, 'User');
  }

  if (
    canBeProxiedUserRoles?.IS_LCM_GLOBAL_ADMIN ||
    canBeProxiedUserRoles?.IS_LCM_ZONE_ADMIN
  ) {
    can(Action.Create, 'Admin');
  }

  if (
    canBeProxiedUserRoles?.IS_LCM_GLOBAL_ADMIN ||
    canBeProxiedUserRoles?.IS_LCM_ZONE_ADMIN
  ) {
    can(Action.Manage, 'IdpEligibility');
  }

  if (canBeProxiedUserRoles?.IS_LCM_GLOBAL_ADMIN) {
    can(Action.Manage, 'RoleSetting');
  }

  if (isAdminOrPbp) {
    can(Action.View, 'Admin');
    can(Action.View, 'Footer');
  }

  if (hasDownloadDocumentsPermission) {
    can(Action.View, 'DownloadDocuments');
  }

  if (hasIdpPermission) {
    can(Action.View, 'Idp');
  }

  if (
    [
      canBeProxiedUserRoles?.IS_LCM_GLOBAL_ADMIN,
      canBeProxiedUserRoles?.IS_PBP,
    ].includes(true)
  ) {
    can(Action.View, 'Stellar');
  }

  if (isManager) {
    can(Action.View, 'ManagerApproval');
  }

  if (
    hasMyTeamResultsPermission &&
    isLiveManager &&
    canAccessResultsBeforeStart
  ) {
    can(Action.View, 'TeamReports');
  }

  if (hasMyTeamIdpPermission) {
    can(Action.View, 'TeamIdp');
  }

  if (
    canSeeMyTeamCompletion &&
    (isInEvaluationPeriod || isInEvaluationReadOnlyPeriod)
  ) {
    can(Action.View, 'TeamAssessments');
  }

  if (canAccessDashboard) {
    if (canAccessDashboardAsManager) {
      can(Action.View, 'DashboardAsManager');
    }
    if (isAdminOrPbp) {
      can(Action.View, 'DashboardAsAdmin');
    }
    can(Action.View, 'NominationsDashboard');
    can(Action.View, 'AssessmentsDashboard');

    if (isInNominationsPeriod) {
      can(Action.View, 'NominationsDashboardMenu');
    }

    if (isInEvaluationPeriod || isInCalculationPeriod) {
      can(Action.View, 'AssessmentsDashboardMenu');
    }
  }

  const ability = build();

  if (rules) {
    ability.update(rules);
  }

  return ability;
}
